import React, { useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IonIcon } from '@ionic/react';
import { 
  listOutline, 
  listCircleOutline,
  codeOutline
} from 'ionicons/icons';
import './RichTextEditor.css';

const RichTextEditor = forwardRef(({ 
  value, 
  onChange, 
  onKeyDown,
  placeholder = 'Type your message...',
  showToolbar = true,
  minHeight = '120px',
  ghostSuggestion = '',
  onAcceptSuggestion
}, ref) => {
  const [showGhost, setShowGhost] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Disable headings for simple email editor
        codeBlock: false
      })
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
        'data-placeholder': placeholder
      },
      handleKeyDown: (view, event) => {
        // Handle Tab for ghost suggestion
        if (event.key === 'Tab' && ghostSuggestion && onAcceptSuggestion) {
          event.preventDefault();
          onAcceptSuggestion();
          return true;
        }
        
        if (onKeyDown) {
          onKeyDown(event);
        }
        return false;
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      
      // Update current text for ghost positioning
      setCurrentText(text);
      
      // Don't trigger onChange if content is just empty paragraph
      if (html === '<p></p>') {
        onChange?.('', '');
      } else {
        onChange?.(html, text);
      }
    }
  });

  // Show/hide ghost suggestion based on cursor position
  useEffect(() => {
    if (editor && ghostSuggestion) {
      setShowGhost(true);
    } else {
      setShowGhost(false);
    }
  }, [ghostSuggestion, editor]);

  // Expose editor methods to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      try {
        if (editor && !editor.isDestroyed) {
          editor.commands.focus();
        }
      } catch (e) {
        // Editor not ready
      }
    },
    clear: () => {
      try {
        if (editor && !editor.isDestroyed) {
          editor.commands.clearContent();
        }
      } catch (e) {
        // Editor not ready
      }
    },
    insertText: (text) => {
      try {
        if (editor && !editor.isDestroyed) {
          editor.commands.insertContent(text);
        }
      } catch (e) {
        // Editor not ready
      }
    },
    getText: () => {
      try {
        return editor && !editor.isDestroyed ? editor.getText() : '';
      } catch (e) {
        return '';
      }
    },
    getHTML: () => {
      try {
        return editor && !editor.isDestroyed ? editor.getHTML() : '';
      } catch (e) {
        return '';
      }
    }
  }));

  // Update editor content when value prop changes (for clearing)
  useEffect(() => {
    try {
      if (value === '' && editor && !editor.isDestroyed && editor.getHTML() !== '<p></p>') {
        editor.commands.setContent('');
      }
    } catch (e) {
      // Editor not ready
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-editor" style={{height: '100%'}}>
      {showToolbar && (
        <div className="editor-toolbar" onClick={(e) => e.preventDefault()}>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            type="button"
            title="Bold (⌘B)"
          >
            <strong style={{ fontSize: '16px', fontWeight: '900' }}>B</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            type="button"
            title="Italic (⌘I)"
          >
            <em style={{ fontSize: '16px', fontStyle: 'italic' }}>I</em>
          </button>
          <div className="toolbar-separator" />
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            type="button"
            title="Bullet List"
          >
            <IonIcon icon={listOutline} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            type="button"
            title="Numbered List"
          >
            <IonIcon icon={listCircleOutline} />
          </button>
          <div className="toolbar-separator" />
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={editor.isActive('code') ? 'is-active' : ''}
            type="button"
            title="Inline Code"
          >
            <IonIcon icon={codeOutline} />
          </button>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        {editor && !editor.isDestroyed && <EditorContent editor={editor} />}
        
        {/* Ghost Suggestion Overlay */}
        {showGhost && ghostSuggestion && editor && !editor.isDestroyed && (
          <div className="tiptap-ghost-overlay">
            <span className="tiptap-ghost-hidden-text">{currentText}</span>
            <span className="tiptap-ghost-suggestion">{ghostSuggestion}</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default RichTextEditor;

